import { List } from "@dropbox/dig-components/list";
import type {
  ChatConversationMessage,
  ChatConversationMessageMessage,
  ChatSource,
} from "@mirage/mosaics/Chat/types";
import { getCitationAndContextSources } from "@mirage/mosaics/Chat/utils/messageSources";
import { getSourceUUID } from "@mirage/shared/compose/compose-session";
import type { SearchFilter } from "@mirage/shared/search/search-filters";
import { memo, useState } from "react";
import { AnswerCitationCard } from "./AnswerCitationCard";
import { AnswerCitationShowMoreCard } from "./AnswerCitationCardShowMoreCard";

const DEFAULT_NUM_CARDS = 5;

interface AnswerCitationCardsProps {
  activeFilters?: SearchFilter[];
  query?: string;
  messages: ChatConversationMessage[];
  conversationId?: string;
  searchSessionId?: string;
  assistRequestId?: string;
  listView?: boolean;
}

export const AnswerCitationCards = memo(
  ({
    messages,
    activeFilters,
    query,
    conversationId,
    searchSessionId,
    assistRequestId,
    listView,
  }: AnswerCitationCardsProps) => {
    const firstResponseMessage = messages.find(
      (message): message is ChatConversationMessageMessage =>
        message.type === "message" && message.role === "assistant",
    );
    let sourcesArray: ChatSource[] = [];
    if (firstResponseMessage) {
      const { citations, context } =
        getCitationAndContextSources(firstResponseMessage);
      if (citations.length > 0) {
        sourcesArray = citations;
      } else if (context.length > 0) {
        sourcesArray = context;
      }
    }

    const [showAll, setShowAll] = useState(false);

    const sourcesToShow =
      !showAll && sourcesArray.length > DEFAULT_NUM_CARDS
        ? sourcesArray.slice(0, DEFAULT_NUM_CARDS - 1)
        : sourcesArray;

    if (sourcesArray.length === 0) {
      return null;
    }
    return (
      <List spacing="small" paddingTop={0}>
        {sourcesToShow.map((source: ChatSource) => (
          <AnswerCitationCard
            activeFilters={activeFilters}
            query={query}
            key={getSourceUUID(source)}
            source={source}
            conversationId={conversationId}
            searchSessionId={searchSessionId}
            assistRequestId={assistRequestId}
            listItem={listView}
          />
        ))}
        {sourcesToShow.length < sourcesArray.length && (
          <AnswerCitationShowMoreCard
            moreSources={sourcesArray.slice(sourcesToShow.length)}
            onClick={() => setShowAll(true)}
            conversationId={conversationId}
            searchSessionId={searchSessionId}
            assistRequestId={assistRequestId}
            listItem={listView}
          />
        )}
      </List>
    );
  },
);
AnswerCitationCards.displayName = "AnswerCitationCards";
